import { Link, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTenantStore } from '../../stores/tenant.store';
import { useAccountingPeriodStore } from '../../stores/accounting-period.store';
import { apiService } from '../../services/api.service';
import { breadcrumbsForPath } from './tabRoutes';

/**
 * Shell del módulo Contabilidad (UX1).
 * Empresa = tenant global; período de consulta = store del módulo.
 */
export function AccountingLayout() {
  const location = useLocation();
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const dateFrom = useAccountingPeriodStore((s) => s.dateFrom);
  const dateTo = useAccountingPeriodStore((s) => s.dateTo);
  const setDateFrom = useAccountingPeriodStore((s) => s.setDateFrom);
  const setDateTo = useAccountingPeriodStore((s) => s.setDateTo);

  const [entityLabel, setEntityLabel] = useState<string>('—');
  const [baseCurrency, setBaseCurrency] = useState<string>('ARS');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!legalEntityId) {
        setEntityLabel('Sin empresa');
        setBaseCurrency('ARS');
        return;
      }
      try {
        const list = await apiService.listLegalEntities();
        const le = (list ?? []).find((e: any) => e.id === legalEntityId);
        if (cancelled) return;
        setEntityLabel(le ? `${le.code} · ${le.legalName}` : legalEntityId);
        setBaseCurrency(le?.baseCurrency || 'ARS');
      } catch {
        if (!cancelled) setEntityLabel(legalEntityId);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [legalEntityId]);

  const crumbs = breadcrumbsForPath(location.pathname);

  return (
    <div className="p-6 space-y-4">
      <header className="space-y-3 border-b border-gray-200 pb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contabilidad</h1>
            <nav className="mt-1 flex flex-wrap items-center gap-1 text-sm text-gray-500" aria-label="Breadcrumb">
              {crumbs.map((c, i) => (
                <span key={`${c.label}-${i}`} className="flex items-center gap-1">
                  {i > 0 && <span className="text-gray-300">/</span>}
                  {c.to && i < crumbs.length - 1 ? (
                    <Link to={c.to} className="hover:text-gray-800">
                      {c.label}
                    </Link>
                  ) : (
                    <span className={i === crumbs.length - 1 ? 'text-gray-800 font-medium' : ''}>
                      {c.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          </div>
          <div className="flex flex-wrap items-end gap-3 text-sm">
            <div>
              <div className="text-xs text-gray-500">Empresa</div>
              <div className="font-medium text-gray-900 max-w-[14rem] truncate" title={entityLabel}>
                {entityLabel}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Moneda base</div>
              <div className="font-medium text-gray-900">{baseCurrency}</div>
            </div>
            <label className="text-sm" id="accounting-period-controls">
              <span className="block text-xs text-gray-500">Desde</span>
              <input
                type="date"
                className="border rounded-md px-2 py-1.5"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Hasta / as of</span>
              <input
                type="date"
                className="border rounded-md px-2 py-1.5"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </label>
          </div>
        </div>
        {!legalEntityId && (
          <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            Seleccioná una <strong>Empresa</strong> en la barra superior para operar contabilidad.
          </div>
        )}
      </header>
      <Outlet />
    </div>
  );
}
